import { useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";

interface MessageFeedbackProps {
  /** Unikátní ID zprávy – slouží jako React key, neposílá se do DB */
  messageId: string;
  /** Zablokuje tlačítka (např. pokud zpráva ještě streamuje) */
  disabled?: boolean;
  /** Callback volaný po kliknutí – závisí na rodiči, jak data uloží */
  onFeedback: (rating: -1 | 1, correction?: string) => Promise<void>;
}

/**
 * Jednoduché thumbs-up / thumbs-down hodnocení AI odpovědi.
 * Po negativním hodnocení nabídne volitelné textové pole pro opravu.
 * Po odeslání zobrazí potvrzení na 2 sekundy.
 */
export function MessageFeedback({ messageId: _messageId, disabled, onFeedback }: MessageFeedbackProps) {
  const [rating, setRating] = useState<-1 | 1 | null>(null);
  const [showCorrection, setShowCorrection] = useState(false);
  const [correction, setCorrection] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const handleThumbsUp = async () => {
    if (rating !== null || saving || disabled) return;
    setRating(1);
    setSaving(true);
    try {
      await onFeedback(1);
    } finally {
      setSaving(false);
      setDone(true);
      setTimeout(() => setDone(false), 2000);
    }
  };

  const handleThumbsDown = () => {
    if (rating !== null || saving || disabled) return;
    setRating(-1);
    setShowCorrection(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onFeedback(-1, correction.trim() || undefined);
    } finally {
      setSaving(false);
      setShowCorrection(false);
      setDone(true);
      setTimeout(() => setDone(false), 2000);
    }
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      await onFeedback(-1);
    } finally {
      setSaving(false);
      setShowCorrection(false);
    }
  };

  if (done) {
    return (
      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1.5 ml-1">
        <span className="text-green-600">✓</span>
        <span>Díky za zpětnou vazbu</span>
      </div>
    );
  }

  return (
    <div className="mt-1.5 ml-1">
      {!showCorrection ? (
        <div className="flex items-center gap-0.5">
          <button
            onClick={handleThumbsUp}
            disabled={disabled || saving || rating !== null}
            className={`p-1.5 rounded-md transition-colors ${
              rating === 1
                ? "text-green-600"
                : "text-muted-foreground/40 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-950/20"
            } disabled:cursor-not-allowed`}
            title="Dobrá odpověď"
          >
            <ThumbsUp className="w-3.5 h-3.5" />
          </button>

          <button
            onClick={handleThumbsDown}
            disabled={disabled || saving || rating !== null}
            className={`p-1.5 rounded-md transition-colors ${
              rating === -1
                ? "text-red-500"
                : "text-muted-foreground/40 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20"
            } disabled:cursor-not-allowed`}
            title="Špatná odpověď"
          >
            <ThumbsDown className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="mt-2 space-y-2 max-w-sm">
          <p className="text-xs text-muted-foreground">Co bylo špatně? (volitelné)</p>
          <textarea
            value={correction}
            onChange={(e) => setCorrection(e.target.value)}
            placeholder="Napiš správnou odpověď nebo co chybí..."
            className="w-full text-xs bg-background border border-border rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-ring"
            rows={3}
            disabled={saving}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {saving ? "Ukládám…" : "Odeslat"}
            </button>
            <button
              onClick={handleSkip}
              disabled={saving}
              className="text-xs text-muted-foreground hover:text-foreground px-2 py-1.5 transition-colors"
            >
              Přeskočit
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
